/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { ChecklistsBusinessService } from '../../resources';
import { ChecklistItem } from '../../resources/checklists.model';
import { AppRouter, getRouteInitData } from '@gh/common-shared';
import { BuilderPermission, ChecklistStatus, SystemRoleBit } from '@gh/builder-shared-data';

@Component({
             selector: 'gh-checklists-list',
             template: require('./checklists-list.component.html'),
             styles: [require('./checklists-list.component.scss')],
           })
export class ChecklistsListComponent implements OnInit {

  checklist: ChecklistItem[];
  showDeleted: boolean = false;

  get isCorpOrMasterView(): boolean {
    return this.context.masterView || this.context.corporateView;
  }

  get isCorpOrMaster(): boolean {
    return this.context.hasSystemRole(SystemRoleBit.Master)
    || this.context.hasSystemRole(SystemRoleBit.Corporate);
  }

  get hasEditPermission(): boolean {
    return this.isCorpOrMaster
      || this.context.hasPermission(BuilderPermission.Setup.Checklists.EDIT)
      || this.context.hasPermission(BuilderPermission.Setup.Checklists.VIEW);
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private context: BuilderUserContext,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private commonActions: CommonActions,
              private pipelines: ListActionPipelines,
              private checklistBusiness: ChecklistsBusinessService) {
  }

  ngOnInit(): void {
    const { checklist } = getRouteInitData(this.route);
    this.checklist = checklist;
  }

  onDropCheckItem(event: CdkDragDrop<ChecklistItem>): void {
    moveItemInArray(this.checklist, event.previousIndex, event.currentIndex);
    this.checklistBusiness.updateOrder(this.checklist).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (checklist: ChecklistItem[]) => {
          this.checklist = checklist;
        },
        (error) => this.notificationService.processAll(error),
      );
  }

  onDelete(template: ChecklistItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
       messages: {
         confirmUsed: {
           id: this.isCorpOrMasterView ? 'msg_checklists_mark_deleted' : 'msg_office_checklist_mark_deleted',
           params: { name: template.name },
         },
         confirmNotUsed: {
           id: 'msg_office_checklist_delete',
           params: { name: template.name },
         },
       },
       executes: {
         checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
                                                  this.checklistBusiness.checkUsage(state.value)),
         summary: this.commonActions.execute((state: CommonFlowState<any>) =>
                                               this.checklistBusiness.deactivateOrDelete(template, state.value)),
       },
       finish: () => {
         this.showDeleted = true;
         this.reload();
       },
      });

    flow.run({
       value: template,
     });
  }

  onUndelete(checklistItem: ChecklistItem): void {
    this.dialogService.confirmation(yesNoConfirmation(this.isCorpOrMasterView ? 'msg_checklists_undelete' :
                                                        'msg_office_checklist_undelete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.checklistBusiness.undelete(checklistItem.id).pipe((this.waitingService.operator<ChecklistItem>()))))
      .subscribe(() => {
        this.reload();
      },         this.notificationService.processAll);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/checklists/new']);
  }

  onEdit(checklistItem: ChecklistItem): void {
    if (!this.hasEditPermission) {
      return;
    }
    this.router.navigateToChild([`/setup/checklists/${checklistItem.id}`]);
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
                                     label: label ? label : 'lbl_mobile_app_checklists',
                                     entityType: entityType ? entityType : 'CHECKLIST',
                                     entityId: entityId ? entityId : undefined,
                                   });
  }

  reload(): void {
    this.checklistBusiness.findAll(this.showDeleted).subscribe((data) => {
      this.checklist = data;
    });
  }

  isActive(item: ChecklistItem): boolean {
    return item.status === ChecklistStatus.ACTIVE;
  }

  canBeDeleted(item: ChecklistItem): boolean {
    if (item.status !== ChecklistStatus.ACTIVE) {
      return false;
    }
    if (this.context.corporateView) {
      return true;
    } else if (this.context.masterView) {
        return !item.parentId && !item.notEditable ;
    } else {
      return !item.parentId && !item.notEditable &&
        (this.isCorpOrMaster || this.context.hasPermission(BuilderPermission.Setup.Checklists.DELETE));
    }
  }

  canBeUnDeleted(item: ChecklistItem): boolean {
    if (item.status === ChecklistStatus.ACTIVE) {
      return false;
    }
    if (this.context.corporateView) {
      return true;
    } else if (this.context.masterView) {
      return !item.notEditable && !item.parentId;
    } else {
      return !item.parentId && !item.notEditable
        && (this.isCorpOrMaster || this.context.hasPermission(BuilderPermission.Setup.Checklists.UNDELETE));
    }
  }
}
